import type { ComponentType, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useNavigate } from 'react-router-dom';

import type { ApiAcceptedLanguage } from '@izimi/api';
import { ApiRole } from '@izimi/api';
import { AuthManager, useAuth } from '@izimi/auth';
import { locales } from '@izimi/core';
import { isUserRole, useModules } from '@izimi/modules';
import config, { appSpec } from 'core/config';

import { Routes } from '../navigation/types';

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const {
    public: { useNotaryConfiguration },
  } = useModules();
  const { configuration } = useNotaryConfiguration();

  return (
    <AuthManager
      apiUrl={config.apiUrl}
      authenticationProviders={configuration?.authenticationProviders}
      onSigninCallback={(user, language) => {
        if (language && locales.includes(language as ApiAcceptedLanguage)) {
          i18n.changeLanguage(language);
        } else if (
          user?.profile?.language &&
          typeof user?.profile?.language === 'string' &&
          locales.includes(user?.profile?.language)
        ) {
          // after sign-in set the ui interface as configured by api
          i18n.changeLanguage(user.profile.language);
        }
        navigate(Routes.Vault, { replace: true });
      }}
      appSpec={appSpec}>
      {children}
    </AuthManager>
  );
};

export const withRedirectToVault = <T extends object>(Component: ComponentType<T>) => {
  return (props: T) => {
    const auth = useAuth();

    if (auth.isAuthenticated) {
      return <Navigate to={Routes.Vault} replace />;
    }

    return <Component {...props} />;
  };
};

export const withAuthenticatedUser =
  <T extends object>(Component: ComponentType<T>) =>
  (props: T) => {
    const auth = useAuth();

    if (!auth.isAuthenticated) {
      return null;
    }

    return <Component {...props} />;
  };

export const withRedirectToLogin = <T extends object>(Component: ComponentType<T>) => {
  return (props: T) => {
    const auth = useAuth();

    if (!auth.isAuthenticated) {
      return <Navigate to={Routes.Login} />;
    }

    return <Component {...props} />;
  };
};

export const withNotaryOnly = <T extends object>(Component: ComponentType<T>) => {
  return (props: T) => {
    const auth = useAuth();

    if (auth.isAuthenticated && auth.user && !isUserRole(ApiRole.Notary, auth.user.profile.roles)) {
      return <Navigate to={Routes.Vault} replace />;
    }
    return <Component {...props} />;
  };
};
